import { Component, OnInit, OnChanges, OnDestroy,
         AfterViewInit, Input, Output,
         EventEmitter, SimpleChanges, ViewChild, ElementRef }
         from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, finalize } from 'rxjs';
import { FormGroup, FormControl } from '@angular/forms';
import { ERROR } from '@app/constants/Error.constant';
import { NotificationService } from 
    '@app/secure/notification/notification.service';
import { DocumentService } from '@app/secure/service/document.service';
import { CyDateTime } from '@app/secure/service/CyDateTime.service';
import { ApiService } from '@app/secure/service/Api.service';

@Component
({
    selector: 'document',
    templateUrl: './document.component.html',
    styleUrls: ['./document.component.scss'],
    standalone: false
})
class DocumentComponent  
          implements OnInit, OnChanges, OnDestroy, AfterViewInit 
{
    @Input() entityType: string;
    @Input() entityUId: string;
    @Input() entityPath: string;
    @Output() onUploadStart: EventEmitter<any> = new EventEmitter<any>();
    @Output() onUploadComplete: EventEmitter<any> = new EventEmitter<any>();
    @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;

    content: any;
    
    // avatar custom css
    customStyle = 
    {
        backgroundColor: "#ffffff",
        border: "1px solid #7e7e7e",
        borderRadius: "50%",
        color: "#7e7e7e",
        cursor: "pointer"
    };
    
    /*
     * upload form 
    */
    uploadForm: FormGroup;
    
    docName: FormControl;
    docDescription: FormControl;
    
    uploadDisabled: boolean;
    
    newDocument = 
    {
        name : null,
        description: null
    };
        
    documents = [];
    
    private subscriptions: Subscription[] = [];
    
    // Make CyDateTime accessible in template
    public CyDateTime = CyDateTime;
    
    /**
     * @desc   - component constructor
     * @access - public
     * @param  - 
     * @return - 
     */                
    constructor
    (            
        private router: Router,    
        private apiService: ApiService,
        protected notificationSvc: NotificationService,
        private documentService: DocumentService
    ) 
    {}    
        
    /**
     * @desc   - initialization of the document page
     * @access - public
     * @param  -
     * @return - 
     */    
    ngOnInit()
    {
        let subscription: Subscription;

        /*
         * upload form
         */
        this.docName = new FormControl();
        this.docDescription = new FormControl();

        this.uploadForm = new FormGroup
        ({
            docName: this.docName,
            docDescription: this.docDescription
        });
        
        this.uploadDisabled = false;

        // Subscribe to document list refresh events
        subscription = this.documentService.refreshDocumentList$
        .subscribe(() => 
        {
            this.updateDocumentList();
        });
        this.subscriptions.push(subscription);
    }
    
    /**
     * @desc   - changes to the document page
     * @access - public
     * @param  -
     * @return - 
     */    
    ngOnChanges
    (
        changes: SimpleChanges
    ) 
    {
        if (changes.entityUId && changes.entityUId.currentValue) 
        {
            this.updateDocumentList();
        }
    }
      
        
    /**
     * @desc   - angular function when the page is initialized
     * @access - public
     * @param  - 
     * @return - 
     */            
    ngAfterViewInit()
    {
    }
    
    /**
     * @desc   - angular function when the page is closing
     * @access - public
     * @param  - 
     * @return - 
     */            
    ngOnDestroy()
    {
        // Clean up subscriptions
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * @desc   - Whether upload form has in-progress draft state
     * @access - public
     * @param  -
     * @return - boolean
     */
    hasUploadFormDraft(): boolean
    {
        let result: boolean;
        let docNameVal: string;
        let docDescVal: string;
        let fileInputEl: HTMLInputElement;
        let hasFiles: boolean;

        result = false;
        docNameVal = '';
        docDescVal = '';
        hasFiles = false;

        if (this.uploadDisabled)
        {
            result = true;
        }
        else
        {
            docNameVal = this.docName.value == null ?
                '' : String(this.docName.value).trim();
            docDescVal = this.docDescription.value == null ?
                '' : String(this.docDescription.value).trim();
            if (docNameVal !== '' || docDescVal !== '')
            {
                result = true;
            }
            else if (this.fileInputRef &&
                this.fileInputRef.nativeElement)
            {
                fileInputEl = this.fileInputRef.nativeElement;
                hasFiles = !!(fileInputEl.files &&
                    fileInputEl.files.length > 0);
                if (hasFiles)
                {
                    result = true;
                }
            }
        }

        return result;
    }

    /**
     * @desc   - Resets upload form fields and file selection
     * @access - public
     * @param  -
     * @return - void
     */
    clearUploadForm(): void
    {
        let i: number;
        let fileInputEl: HTMLInputElement;

        this.docName.setValue('');
        this.docDescription.setValue('');
        this.uploadDisabled = false;
        if (this.fileInputRef && this.fileInputRef.nativeElement)
        {
            fileInputEl = this.fileInputRef.nativeElement;
            fileInputEl.value = '';
        }
        for (i = 0; i < this.documents.length; i++)
        {
            this.documents[i].edit = true;
        }
    }

    /**
     * @desc   - onvert the input value of the docName form control to uppercase
     * @access - public
     * @param  -
     * @return - 
     */
    onDocNameInput
    (): void 
    {
        let docNameControl;
        let upperCaseValue;
        let rawValue: string;

        docNameControl = this.uploadForm.get('docName');
        rawValue = docNameControl.value == null ?
            '' : String(docNameControl.value).trim();
        if (rawValue !== '')
        {
            upperCaseValue = String(docNameControl.value).toUpperCase();
            docNameControl.setValue(upperCaseValue, { emitEvent: false });
        }
    }

    /**
     * @desc   - onvert the input value of the docDescription form control to uppercase
     * @access - public
     * @param  -
     * @return -
     */
    onDocDescriptionInput
    (): void 
    {
        let docDescriptionControl;
        let upperCaseValue;
        let rawValue: string;

        docDescriptionControl = this.uploadForm.get('docDescription');
        rawValue = docDescriptionControl.value == null ?
            '' : String(docDescriptionControl.value).trim();
        if (rawValue !== '')
        {
            upperCaseValue =
                String(docDescriptionControl.value).toUpperCase();
            docDescriptionControl.setValue(
                upperCaseValue,
                { emitEvent: false }
            );
        }
    }

    /**
     * @desc   - check if the form is modified
     * @access - public
     * @param  - 
     * @return - 
     */ 
    checkDirty
    (
        originalValue: string, 
        formControlName: string
    ): void 
    {
        let control;

        control = this.uploadForm.get(formControlName);
        if (originalValue === control.value) 
        {
            control.markAsPristine();
        }
    }
    
    /**
     * @desc   - update the docucment name and description
     * @access - private
     * @param  - string documentId : document DB id 
     * @param  - string documentName : document name 
     * @param  - string documentDescription : document description 
     * @return - 
     */            
    private async updateDocument
    (
        documentId,    
        documentName,
        documentDescription
    )
    {
        let documentData;
        let url;
        let data;
        let svcResponse;
        let response;

        try
        {
            data = 
            {
                documentId: documentId,    
                documentName: documentName,
                documentDescription: documentDescription
            };
            
            response = {};
            svcResponse = await this.apiService.postRaw<any>(
                'document/updateDocument',
                data
            ).toPromise();
            
            if (svcResponse.error == ERROR.NO_ERROR)
            {
                response.error = ERROR.NO_ERROR;
                response.byUser = svcResponse.cyData?.byUser;
            }
            else
            {
                response.error = ERROR.UPDATE_FAILED;    
            }
        }
        catch (e)
        {
            console.log(e);
            response.error = ERROR.UPDATE_FAILED;
        }
        
        return response;        
    }

    /**
     * @desc   - delete the docucment
     * @access - private
     * @param  - string documentId : document DB id 
     * @return - 
     */            
    private async deleteDocumentService
    (
        documentId    
    )
    {
        let documentData;
        let url;
        let data;
        let svcResponse;
        let response;

        try
        {
            data = 
            {
                documentId: documentId,    
            };
            
            response = {};
            svcResponse = await this.apiService.postRaw<any>(
                'document/deleteDocument',
                data
            ).toPromise();
            
            if (svcResponse.error == ERROR.NO_ERROR)
            {
                response.error = ERROR.NO_ERROR;
            }
            else
            {
                response.error = ERROR.UPDATE_FAILED;    
            }
        }
        catch (e)
        {
            console.log(e);
            response.error = ERROR.UPDATE_FAILED;
        }
        
        return response;        
    }
    
    /**
     * @desc   - update the document list
     * @access - public
     * @param  - 
     * @return - 
     */            
    public async updateDocumentList()
    {
        let i;
        let document;
        let documentData;
        let fileTypeRef;
        let url;
        let data;
        let svcResponse;
        let response;

        try
        {
            // TODO - need to define constant for file type 
            data = {entityType: this.entityType,
                    entityUId: this.entityUId
            };
            
            response = {};
            svcResponse = await this.apiService.postRaw<any>(
                'document/getDocuments',
                data
            ).toPromise();
            
            if (svcResponse.error == ERROR.NO_ERROR)
            {
                response.error = ERROR.NO_ERROR;
                if (svcResponse.cyData.files == null)
                {
                    response.error = ERROR.NO_MATCH 
                }
                else
                {
                    response.error = ERROR.NO_ERROR;
                    response.files = svcResponse.cyData.files;
                    
                    // reset the documents
                    this.documents = [];
                    
                    for (i=0;i<response.files.length;i++)
                    {
                        documentData = response.files[i];
                        fileTypeRef = documentData &&
                            documentData.fileTypeREF ?
                            String(documentData.fileTypeREF).toLowerCase() :
                            '';

                        if (fileTypeRef == 'html')
                        {
                            continue;
                        }

                        document = {};
                        document.edit = true;                        
                        document.id = documentData.id;
                        document.entityUId = documentData.entityUId;
                        document.entityType = documentData.entityType;
                        document.fileTypeREF = documentData.fileTypeREF;
                        document.path = documentData.path;
                        document.name = documentData.name;
                        document.description = documentData.description;
                        document.byUser = documentData.byUser;
                        document.createDT = documentData.createDT;
                        this.documents.push(document);
                    }
                }
            }
            else
            {
                response.error = ERROR.MATCH_FAILED;    
            }
        }
        catch(e)
        {
            console.log(e);
            response.error = ERROR.MATCH_FAILED;
        }
        
        return response;
    }
    
    /**
    * @desc   - get document type
    * @access - public
    * @param  - string fileTypeREF : file type for the document
    * @return - 
    */            
    private getBlobType
    (
        fileTypeREF
    )
    {
        let blobType;
        
        if (fileTypeREF == 'png')
        {
            blobType = 'image/png';
        }
        else if (fileTypeREF == 'pdf')
        {
            blobType = 'application/pdf';
        }    
        else if (fileTypeREF == 'html')
        {
            blobType = 'text/html';
        }
        else if (fileTypeREF == 'jpeg' ||  fileTypeREF == 'jpg')
        {
             blobType = 'image/jpeg';
        }    
        else
        {
            blobType =  '';
        }
        
        return blobType;
    }
                    
    /**
    * @desc   - get the document content
    * @access - private
    * @param  - object oneDocument : document object
    * @return - 
    */            
    private getDocumentContent
    (
        oneDocument
    )
    {
        this.documentService
        .getDocumentContent(oneDocument)
        .subscribe((data: Blob) =>
        {
            
            // need to know the type of data
            // for image use this
            /*
             *  createImageFromBlob(image: Blob) {
             *  
             *  
             *                  let reader = new FileReader();
                    reader.addEventListener("load", () => {
                        this.imageBlobUrl = reader.result;
                    }, false);

                    if (image) {
                        reader.readAsDataURL(image);
                    }
                }
            */
            // let a;
            
//            let file = new Blob([data], {type: 'application/png'});
            let blobType = this.getBlobType(oneDocument.fileTypeREF);
            let file = new Blob([data], {type: blobType});
//            let file = new Blob([data], {});
            let fileURL = URL.createObjectURL(file);
            
            this.onUploadComplete.emit();
            window.open(fileURL);
            /*
            a = document.createElement('a');
            a.href = fileURL;
            a.target = '_blank';
            a.download = 'test.pdf';
            document.body.appendChild(a);
            a.click();
            */
        },
        (error) => 
        {
            console.log('Download File has failed: ', error);
        });
    }
    
    /**
     * @desc   - select the files 
     * @access - public
     * @param  - object event: event object for file selection
     * @return - 
     */            
    onFileSelected
    (
        event: any
    ): void 
    {
        let files: FileList;
        let pathValue: string;
        let entityPathStr: string;
        let formData: FormData;
        let i: number;
        let msg: string;
        let nameTrim: string;
        let descTrim: string;
        let fileInputEl: HTMLInputElement;
        let file: any;
        let oneDocument: any;
        let failMsg: string;

        msg = `Document was successfully uploaded. ` +
            `Please wait to complete the processing`;
        files = null;
        pathValue = '';
        entityPathStr = '';

        files = event && event.target && event.target.files ?
            event.target.files : null;

        if (files && files.length > 0)
        {
            entityPathStr = typeof this.entityPath === 'string' ?
                this.entityPath : '';
            if (entityPathStr.trim() !== '')
            {
                pathValue = entityPathStr;
            }
            else if (this.entityUId)
            {
                pathValue = String(this.entityUId);
            }
            else
            {
                pathValue = '';
            }

            if (!this.entityUId || pathValue === '')
            {
                this.notificationSvc.error(
                    'Store Document',
                    'Save the case motion before uploading documents.',
                    5000
                );
                if (this.fileInputRef && this.fileInputRef.nativeElement)
                {
                    fileInputEl = this.fileInputRef.nativeElement;
                    fileInputEl.value = '';
                }
            }
            else
            {
                this.onUploadStart.emit();
                formData = new FormData();
                formData.append('entityType', this.entityType);
                formData.append('entityUId', this.entityUId);
                formData.append('entityPath', pathValue);
                nameTrim = this.docName.value == null ?
                    '' : String(this.docName.value).trim();
                if (nameTrim !== '')
                {
                    formData.append('documentName', this.docName.value);
                }
                descTrim = this.docDescription.value == null ?
                    '' : String(this.docDescription.value).trim();
                if (descTrim !== '')
                {
                    formData.append(
                        'documentDescription',
                        this.docDescription.value
                    );
                }
                for (i = 0; i < files.length; i++)
                {
                    formData.append('files', files[i]);
                }

                this.apiService.postRawFormData<any>(
                    'document/storeDocument',
                    formData
                )
                .pipe(
                    finalize(() =>
                    {
                        this.onUploadComplete.emit();
                    })
                )
                .subscribe(
                {
                    next: (response) =>
                    {
                        file = response?.cyData?.file;

                        if (
                            !response ||
                            response.error !== ERROR.NO_ERROR ||
                            !file ||
                            file.id === undefined ||
                            file.id === null
                        )
                        {
                            failMsg =
                                'Document upload did not complete. ' +
                                'Storage may be unavailable or ' +
                                'access denied.';
                            this.notificationSvc.error(
                                'Store Document',
                                failMsg,
                                5000
                            );
                        }
                        else
                        {
                            oneDocument = {};
                            oneDocument.edit = true;
                            oneDocument.id = file.id;
                            oneDocument.entityType = file.entityType;
                            oneDocument.entityUId = file.entityUId;
                            oneDocument.fileTypeREF = file.fileTypeREF;
                            oneDocument.path = file.path;
                            oneDocument.name = file.name;
                            oneDocument.description = file.description;
                            oneDocument.byUser = file.byUser;
                            oneDocument.createDT = file.createDT;
                            this.documents.push(oneDocument);

                            this.notificationSvc.info(
                                'Store Document',
                                msg,
                                3000
                            );

                            this.docName.setValue('');
                            this.docDescription.setValue('');
                            if (this.fileInputRef &&
                                this.fileInputRef.nativeElement)
                            {
                                fileInputEl =
                                    this.fileInputRef.nativeElement;
                                fileInputEl.value = '';
                            }
                        }
                    },
                    error: (err) =>
                    {
                        this.notificationSvc.error(
                            'Store Document',
                            'Document upload failed. Please try again.',
                            5000
                        );
                        console.log(
                            'Store document request failed: ',
                            err
                        );
                    }
                });
            }
        }
    }
    
    /**
     * @desc   - handle avatar clicks
     * @access - public
     * @param  - object event: event object for avatar selection
     * @param  - object document: document OBJECT 
     * @return - 
     */            
    avatarClicked
    (
        event: any, 
        oneDocument
    )
    {
        this.onUploadStart.emit();
        this.getDocumentContent(oneDocument);
    }

    /**
     * @desc   - get document type
     * @access - public
     * @param  - string fileTypeREF : file type for the document
     * @return - 
     */            
    getDocumentFileType
    (
        fileTypeREF
    )
    {
        let fileTypeLabel;
        
        if (fileTypeREF == 'png')
        {
            fileTypeLabel = 'P N G';
        }
        else if (fileTypeREF == 'pdf')
        {
            fileTypeLabel = 'P D F';
        }
        else if (fileTypeREF == 'html')
        {
            fileTypeLabel = 'H T M';
        }
        else if (fileTypeREF == 'jpeg')
        {
            fileTypeLabel = 'J P E';
        } 
        else if (fileTypeREF == 'jpg')
        {
            fileTypeLabel = 'J P G';
        }
        else
        {
            fileTypeLabel =  'U N K';
        }
        
        return fileTypeLabel;
    }
        
    /**
     * @desc   - get document label
     * @access - public
     * @param  - 
     * @return - 
     */            
    getDocumentLabel
    (
        type
    )
    {
        let label;
        
        if (type == 'VIN')
        {
            label = 'VIN';
        }
        else if (type == 'CASE')
        {
            label = 'CASE';
        }    
        
        return label;
    }
    
    /**
     * @desc   - determine if the row is new.
     *           Eac row has maximum of 4 columns
     * @access - public
     * @param  - 
     * @return - 
     */            
    isNewRow
    (
        index
    )
    {
        let newRow
        let rowNum;
        
        if (index >0)
        {
            rowNum = index % 4;
            
            if (rowNum == 0)
            {
                newRow = true;        
            }
            else
            {
                newRow = false;
            }
        }
        else
        {
            newRow = false;
        }
        
        return newRow;
    }
    
    /**
     * @desc   - edit the document
     * @access - public
     * @param  - 
     * @return - 
     */            
    editDocument
    (
        index,
        document
    )
    {
        let i;
        
        for (i=0;i<this.documents.length;i++)
        {
            this.documents[i].edit = true;
        }
        
        document.edit = false;
        this.documents[index] = document;
        this.docName.setValue(document.name);
        this.docDescription.setValue(document.description);
        
        this.uploadDisabled = true;
    }
        
    /**
     * @desc   - save the document
     * @access - public
     * @param  - 
     * @return - 
     */            
    async saveDocument
    (
        index,
        document
    )
    {
        let i;
        let updateResult;
        
        let msg = `Document was successfully updated. ` + 
                  `Please wait to complete the processing`;
        let failedMsg = `Document update has failed.`; 
               
        document.name = this.docName.value;
        document.description = this.docDescription.value;
        
        this.onUploadStart.emit();
        updateResult = await this.updateDocument(document.id,
                                                 document.name, 
                                                 document.description);
        this.onUploadComplete.emit();
        
        if (updateResult.error == ERROR.NO_ERROR)
        {
            this.notificationSvc.info('Update Document', msg, 3000);
            
            setTimeout(() => 
            {
                for (i=0;i<this.documents.length;i++)
                {
                    this.documents[i].edit = true;
                }
                       
                document.edit = true;
                // Update the byUser field with the value returned from server
                if (updateResult.byUser)
                {
                    document.byUser = updateResult.byUser;
                }
                this.documents[index] = document;
                
                this.uploadDisabled = false;
            
                // reset document value and description
                this.docName.setValue('');
                this.docDescription.setValue('');                
            }, 3010);        
        }
        else
        {
            // change title base on file.entityType
            this.notificationSvc.error('Update Document', failedMsg, 3000);
        }
    }

    /**
     * @desc   - save the document
     * @access - public
     * @param  - 
     * @return - 
    */            
    cancelEdit
    (
        index,
        document
    )
    {
        let i;
      
        for (i=0;i<this.documents.length;i++)
        {
            this.documents[i].edit = true;
        }
      
        document.edit = true;
        this.documents[index] = document;
        this.docName.setValue('');
        this.docDescription.setValue('');
        
        this.uploadDisabled = false;
    }
        
    /**
     * @desc   - save the document
     * @access - public
     * @param  - 
     * @return - 
     */            
    async deleteDocument
    (
       index,
       document
    )
    {
        let i;
        let deleteResult;
        let msg = `Document was successfully deleted. ` + 
                  `Please wait to complete the processing`;
        let failedMsg = `Document update has failed.`; 
                        
        this.onUploadStart.emit();                
        deleteResult = await this.deleteDocumentService(document.id);
        this.onUploadComplete.emit();

        if (deleteResult.error == ERROR.NO_ERROR)
        {
            this.notificationSvc.info('Update Document', msg, 3000);                
            setTimeout(() => 
            {
                for (i=0;i<this.documents.length;i++)
                {
                    this.documents[i].edit = true;
                }
                       
                document.edit = true;
                this.documents[index] = document;
                
                this.uploadDisabled = false;
            
                // reset document value and description
                this.docName.setValue('');
                this.docDescription.setValue('');
                this.documents.splice(index, 1);                
            }, 3010);
        }  
        else
        {
            // change title base on file.entityType
            this.notificationSvc.error('Delete Document', failedMsg, 3000);
        }
    }       
}
        
export { DocumentComponent }
